import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, ArrowRight, Save, MessageSquare, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function EvaluarEstudiante() {
  const { id } = useParams();
  const { pathname } = useLocation();
  const seg = pathname.split("/")[1];
  const base = `/${["admin", "docente", "franquiciado"].includes(seg) ? seg : "docente"}/estudiante/${id}`; // v5.6.1 · prefijo según el rol
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [notes, setNotes] = useState({});
  const [globalNotes, setGlobalNotes] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get(`/users/${id}`),
      api.get("/evaluations/emotional"),
    ]).then(([s, e]) => {
      setStudent(s.data);
      setData(e.data);
    });
  }, [id]);

  if (!student || !data) return <p className="text-[#8A9499]">Cargando entrevista…</p>;

  const q = data.questions[idx];
  const total = data.questions.length;
  const progress = Math.round(((Object.keys(answers).length) / total) * 100);

  const submit = async () => {
    try {
      const { data: res } = await api.post("/evaluations/conduct", {
        student_id: id,
        answers,
        teacher_notes: globalNotes,
      });
      setDone(res);
      toast.success("Perfil emocional actualizado.");
    } catch {
      toast.error("Error al guardar la entrevista.");
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center" data-testid="evaluation-conducted-done">
        <div className="w-16 h-16 mx-auto rounded-full bg-[#7A9E7E]/15 flex items-center justify-center mb-4">
          <CheckCircle2 size={28} className="text-[#7A9E7E]" />
        </div>
        <h1 className="font-display text-3xl font-medium text-[#2C3539]">Entrevista completada</h1>
        <p className="text-[#5C666B] mt-3 max-w-md mx-auto">{done.message}</p>
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-lg mx-auto">
          {Object.entries(done.result).map(([dim, val]) => (
            <div key={dim} className="bento-card p-3">
              <p className="text-[10px] uppercase tracking-[0.14em] text-[#8A9499] font-bold capitalize">{dim}</p>
              <p className="font-display text-2xl text-[#C87A5B] mt-1">{val}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 flex gap-3 justify-center">
          <button
            onClick={() => navigate(base)}
            className="bg-[#C87A5B] text-white px-6 py-2.5 rounded-md font-medium hover:bg-[#B36B4F]"
            data-testid="back-to-student"
          >Ver perfil del estudiante</button>
          <button
            onClick={() => navigate(`${base}/plan/editar`)}
            className="bg-white border border-[#E8E2D9] text-[#2C3539] px-6 py-2.5 rounded-md font-medium hover:bg-[#F3EFEA]"
            data-testid="go-edit-plan"
          >Armar ruta de mejora</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="evaluar-estudiante-page">
      <Link to={base} className="inline-flex items-center gap-1.5 text-sm text-[#5C666B] hover:text-[#C87A5B]">
        <ArrowLeft size={15} /> Volver al perfil
      </Link>

      <div className="premium-card p-6 flex items-center gap-4">
        <img src={student.avatar} alt="" className="w-14 h-14 rounded-full bg-[#F3EFEA] border border-[#E8E2D9]" />
        <div className="flex-1">
          <span className="tag-premium">Modo entrevista 1:1</span>
          <h1 className="font-display text-2xl font-medium text-[#2C3539] mt-1">Evaluar a {student.name}</h1>
          <p className="text-sm text-[#5C666B]">Lee la pregunta en voz alta, observa y marca la respuesta del estudiante. Toma notas si quieres.</p>
        </div>
      </div>

      <div>
        <div className="flex justify-between text-xs text-[#8A9499] mb-1.5">
          <span>{Object.keys(answers).length} de {total} respondidas</span>
          <span>{progress}%</span>
        </div>
        <div className="h-1.5 bg-[#F3EFEA] rounded-full overflow-hidden">
          <div className="h-full bg-[#C87A5B] rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div className="premium-card p-6 md:p-8" data-testid={`teacher-q-${q.id}`}>
        <div className="flex items-center justify-between text-xs">
          <span className="tag-pill bg-[#F3EFEA] text-[#5C666B] capitalize">{q.dimension}</span>
          <span className="text-[#8A9499]">Pregunta {idx + 1} / {total}</span>
        </div>
        <h2 className="font-display text-2xl md:text-3xl font-medium text-[#2C3539] mt-3 leading-snug">"{q.text}"</h2>
        <p className="text-xs text-[#8A9499] mt-2">Lee al estudiante y marca su respuesta.</p>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-5 gap-2">
          {data.options.map((o) => (
            <button
              key={o.value}
              data-testid={`teacher-opt-${q.id}-${o.value}`}
              onClick={() => setAnswers({ ...answers, [q.id]: o.value })}
              className={`px-3 py-3 rounded-md border text-sm font-medium transition-all ${
                answers[q.id] === o.value
                  ? "bg-[#C87A5B] text-white border-[#C87A5B]"
                  : "bg-white border-[#E8E2D9] text-[#2C3539] hover:border-[#C87A5B]"
              }`}
            >
              <div className="font-display text-xl">{o.value}</div>
              <div className="text-[11px] opacity-90 mt-0.5">{o.label}</div>
            </button>
          ))}
        </div>

        <div className="mt-5">
          <label className="text-xs tracking-[0.14em] uppercase font-bold text-[#8A9499] flex items-center gap-1.5">
            <MessageSquare size={12} /> Nota de observación (opcional)
          </label>
          <textarea
            data-testid={`teacher-note-${q.id}`}
            value={notes[q.id] || ""}
            onChange={(e) => setNotes({ ...notes, [q.id]: e.target.value })}
            placeholder="Ej: respondió con duda y miró hacia abajo…"
            className="mt-2 w-full bg-[#FAF7F2] border border-[#E8E2D9] rounded-md p-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C87A5B]/40 min-h-[70px]"
          />
        </div>

        <div className="flex items-center justify-between mt-6">
          <button
            disabled={idx === 0}
            onClick={() => setIdx(idx - 1)}
            className="text-sm text-[#5C666B] disabled:opacity-40"
            data-testid="teacher-prev"
          >Atrás</button>
          {idx < total - 1 ? (
            <button
              disabled={!answers[q.id]}
              onClick={() => setIdx(idx + 1)}
              className="inline-flex items-center gap-2 bg-[#C87A5B] text-white px-6 py-2.5 rounded-md text-sm font-medium hover:bg-[#B36B4F] disabled:opacity-40"
              data-testid="teacher-next"
            >Siguiente <ArrowRight size={15} /></button>
          ) : (
            <button
              disabled={Object.keys(answers).length < total}
              onClick={submit}
              className="inline-flex items-center gap-2 bg-[#C87A5B] text-white px-6 py-2.5 rounded-md text-sm font-medium hover:bg-[#B36B4F] disabled:opacity-40"
              data-testid="teacher-finish"
            ><Save size={15} /> Guardar entrevista</button>
          )}
        </div>
      </div>

      <div className="premium-card p-5">
        <label className="text-xs tracking-[0.14em] uppercase font-bold text-[#8A9499]">Resumen general (opcional)</label>
        <textarea
          data-testid="teacher-global-notes"
          value={globalNotes}
          onChange={(e) => setGlobalNotes(e.target.value)}
          placeholder="Impresión general del estudiante durante la entrevista…"
          className="mt-2 w-full bg-[#FAF7F2] border border-[#E8E2D9] rounded-md p-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C87A5B]/40 min-h-[80px]"
        />
      </div>
    </div>
  );
}
